/**
 * Cloudflare Turnstile utility for GonkaGame configured domain domain.
 * Mirrors the API of recaptcha.ts for consistent usage.
 */

const DEFAULT_TURNSTILE_SITE_KEY = '0x4AAAAAACOch75L3mNa5SxL'
const TURNSTILE_SITE_KEY =
    import.meta.env.VITE_TURNSTILE_SITE_KEY ?? DEFAULT_TURNSTILE_SITE_KEY

declare global {
    interface Window {
        turnstile?: {
            render(
                container: HTMLElement | string,
                parameters: {
                    sitekey: string
                    theme?: 'light' | 'dark' | 'auto'
                    callback?: (token: string) => void
                    'error-callback'?: () => void
                    'expired-callback'?: () => void
                    execution?: 'eager' | 'execute'
                }
            ): string
            reset(widgetId?: string): void
            execute(widgetId?: string): void
            getResponse(widgetId?: string): string
            remove(widgetId?: string): void
        }
    }
}

let loadPromise: Promise<void> | null = null

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

export const getTurnstileSiteKey = (): string => TURNSTILE_SITE_KEY

/**
 * Load Cloudflare Turnstile script.
 * The script is loaded once and cached.
 */
export const loadTurnstile = (): Promise<void> => {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Turnstile недоступен в SSR среде'))
    }

    if (window.turnstile?.render) {
        return Promise.resolve()
    }

    if (loadPromise) {
        return loadPromise
    }

    loadPromise = new Promise<void>((resolve, reject) => {
        const existingScript = document.querySelector<HTMLScriptElement>(
            `script[src^="https://challenges.cloudflare.com/turnstile"]`
        )

        if (existingScript) {
            const checkReady = () => {
                if (window.turnstile?.render) {
                    resolve()
                } else {
                    setTimeout(checkReady, 50)
                }
            }
            checkReady()
            return
        }

        const script = document.createElement('script')
        script.src = TURNSTILE_SRC
        script.async = true
        script.defer = true
        script.onload = () => {
            const checkReady = () => {
                if (window.turnstile?.render) {
                    resolve()
                } else {
                    setTimeout(checkReady, 50)
                }
            }
            checkReady()
        }
        script.onerror = (e) => {
            console.error('[GonkaGame] Failed to load Turnstile script', e)
            script.remove()
            loadPromise = null
            reject(new Error('Не удалось загрузить Cloudflare Turnstile'))
        }
        document.head.appendChild(script)
    })

    return loadPromise
}

/**
 * Render a Turnstile widget in the given container.
 * Returns the widget ID for later operations.
 */
export const renderTurnstileWidget = async (
    containerId: string,
    callbacks: {
        onSuccess?: (token: string) => void
        onError?: () => void
        onExpired?: () => void
    } = {}
): Promise<string> => {
    await loadTurnstile()

    if (!window.turnstile) {
        throw new Error('Cloudflare Turnstile недоступен')
    }

    const widgetId = window.turnstile.render(`#${containerId}`, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'light',
        execution: 'execute', // Manual execution to prevent auto-submission on load
        callback: callbacks.onSuccess,
        'error-callback': callbacks.onError,
        'expired-callback': callbacks.onExpired,
    })

    return widgetId
}

/**
 * Manually trigger Turnstile verification (used when execution is 'execute').
 */
export const executeTurnstileWidget = (widgetId: string): void => {
    if (window.turnstile) {
        window.turnstile.execute(widgetId)
    }
}

/**
 * Reset a Turnstile widget to allow re-verification.
 */
export const resetTurnstileWidget = (widgetId: string): void => {
    if (window.turnstile) {
        try {
            window.turnstile.reset(widgetId)
        } catch {
            // Ignore reset errors
        }
    }
}

/**
 * Get the current response token from a widget.
 */
export const getTurnstileResponse = (widgetId: string): string => {
    if (window.turnstile) {
        return window.turnstile.getResponse(widgetId) || ''
    }
    return ''
}

/**
 * Remove a widget from the page.
 */
export const removeTurnstileWidget = (widgetId: string): void => {
    if (window.turnstile) {
        try {
            window.turnstile.remove(widgetId)
        } catch {
            // Ignore remove errors
        }
    }
}
